import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

const RECONEXION_INICIAL_MS = 1_000;
const RECONEXION_MAX_MS = 30_000;
const CHEQUEO_CONEXION_MS = 10 * 60 * 1_000;
const ESPERA_PONG_MS = 10_000;

type Sobre<T> = { tipo: string; data?: T };

/**
 * Se conecta a un canal WebSocket del API (`/ws/...`) y emite el `data` de cada mensaje cuyo
 * `tipo` coincide. Reintenta para siempre con backoff (1 s → 30 s) y cada 10 minutos comprueba
 * la conexión con un ping; si no hay pong la cierra y reconecta. Se cierra al desuscribirse.
 */
export function conectarCanalWs<T>(path: string, tipo: string): Observable<T> {
  const url = environment.apiUrl.replace(/^http/, 'ws') + path;

  return new Observable<T>(subscriber => {
    let socket: WebSocket | null = null;
    let cerradoManualmente = false;
    let esperaReconexion = RECONEXION_INICIAL_MS;
    let reconexionTimeout: ReturnType<typeof setTimeout> | null = null;
    let pongTimeout: ReturnType<typeof setTimeout> | null = null;

    const limpiarPong = () => {
      if (pongTimeout) {
        clearTimeout(pongTimeout);
        pongTimeout = null;
      }
    };

    const programarReconexion = () => {
      if (cerradoManualmente || reconexionTimeout) return;
      console.warn(`WS ${path}: reconectando en ${esperaReconexion / 1000}s`);
      reconexionTimeout = setTimeout(() => {
        reconexionTimeout = null;
        connect();
      }, esperaReconexion);
      esperaReconexion = Math.min(esperaReconexion * 2, RECONEXION_MAX_MS);
    };

    const connect = () => {
      limpiarPong();
      const ws = new WebSocket(url);
      socket = ws;

      ws.onopen = () => {
        esperaReconexion = RECONEXION_INICIAL_MS;
      };

      ws.onmessage = event => {
        let sobre: Sobre<T>;
        try {
          sobre = JSON.parse(event.data);
        } catch {
          return;
        }
        if (sobre.tipo === 'pong') {
          limpiarPong();
        } else if (sobre.tipo === tipo) {
          subscriber.next(sobre.data as T);
        }
      };

      ws.onerror = () => {
        console.error(`WS ${path}: error de conexión`);
      };

      ws.onclose = () => {
        // Ignora cierres de sockets viejos que ya fueron reemplazados
        if (socket !== ws) return;
        limpiarPong();
        socket = null;
        programarReconexion();
      };
    };

    const reconectarYa = () => {
      if (cerradoManualmente) return;
      if (reconexionTimeout) {
        clearTimeout(reconexionTimeout);
        reconexionTimeout = null;
      }
      esperaReconexion = RECONEXION_INICIAL_MS;
      const anterior = socket;
      socket = null;
      anterior?.close();
      connect();
    };

    const comprobarConexion = () => {
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        if (!reconexionTimeout) reconectarYa();
        return;
      }
      socket.send(JSON.stringify({ tipo: 'ping' }));
      limpiarPong();
      pongTimeout = setTimeout(() => {
        console.warn(`WS ${path}: sin pong, reconectando`);
        reconectarYa();
      }, ESPERA_PONG_MS);
    };

    const alVolverEnLinea = () => reconectarYa();

    connect();
    const chequeoInterval = setInterval(comprobarConexion, CHEQUEO_CONEXION_MS);
    window.addEventListener('online', alVolverEnLinea);

    return () => {
      cerradoManualmente = true;
      clearInterval(chequeoInterval);
      window.removeEventListener('online', alVolverEnLinea);
      if (reconexionTimeout) clearTimeout(reconexionTimeout);
      limpiarPong();
      const actual = socket;
      socket = null;
      actual?.close();
    };
  });
}
